export const SUGGESTION_INITIAL_VISIBLE = 3
export const SUGGESTION_DEBOUNCE_MS = 400
export const SUGGESTION_REQUEST_LIMIT = 12

export const SUGGESTION_RELATIONS = {
  same_player: { label: 'Same player', tone: 'info' },
  correlated: { label: 'Correlated', tone: 'success' },
  same_game: { label: 'Same game', tone: 'neutral' },
}

export function suggestionRelation(relation) {
  return SUGGESTION_RELATIONS[relation] || { label: relation, tone: 'neutral' }
}

export function suggestionSubtitle(selectedLegs = []) {
  if (selectedLegs.length === 1) return `Related to ${selectedLegs[0].selection_name}`
  if (selectedLegs.length > 1) return `Related to your ${selectedLegs.length} selected legs`
  return ''
}

// Whether a suggestions answer still speaks for the selection: every selected
// leg is one it was asked about (`requested`, which the panel records) or one
// of its suggestions, whose `conflicts` say what each refuses. A leg added
// since from search is neither, so a row can pair it with a leg the book
// refuses (CIN -11 beside a moneyline whose `conflicts` only knew CIN -9.5)
// until the answer for the current selection arrives.
export function suggestionsCoverSelection(response, selectedLegs = []) {
  if (!response) return false
  const known = new Set([
    ...(response.requested || []).map(String),
    ...(response.suggestions || []).map((suggestion) => String(suggestion.leg?.outcome_id)),
  ])
  return selectedLegs.every((leg) => known.has(String(leg.outcome_id)))
}

// The server excludes the selection it was asked about and what the book
// refuses beside it; the selection can have moved on since, so drop anything
// that is now taken or that lists a now-selected leg among its `conflicts`.
// A game holding two selected legs is the pack's one same-game group (the
// engine prices one per pack), however its second leg was added, so only its
// own suggestions stay; two such games leave nothing to add.
export function filterAddableSuggestions(suggestions = [], selectedLegs = []) {
  const outcomes = new Set(selectedLegs.map((leg) => String(leg.outcome_id)))
  const markets = new Set(
    selectedLegs.filter((leg) => leg.market_id).map((leg) => `${leg.event_id}\u0000${leg.market_id}`),
  )
  const legsPerGame = new Map()
  for (const leg of selectedLegs) {
    const game = String(leg.event_id ?? '')
    legsPerGame.set(game, (legsPerGame.get(game) || 0) + 1)
  }
  const grouped = [...legsPerGame].filter(([, count]) => count > 1).map(([game]) => game)
  return suggestions.filter(({ leg, conflicts }) => (
    leg
    && !outcomes.has(String(leg.outcome_id))
    && !markets.has(`${leg.event_id}\u0000${leg.market_id}`)
    && !(conflicts || []).some((id) => outcomes.has(String(id)))
    && (grouped.length === 0 || (grouped.length === 1 && String(leg.event_id ?? '') === grouped[0]))
  ))
}

// Why Add all left a suggestion out: the book refuses it beside one already
// taken, or the two are legs of different games, each of which would grow
// into a same-game group where a pack holds one.
export const SKIP_REFUSED = 'refused'
export const SKIP_SAME_GAME_GROUP = 'same_game_group'

function skipReason(suggestion, because) {
  const game = suggestion.leg.event_id
  const other = because.leg.event_id
  return game != null && other != null && String(game) !== String(other) ? SKIP_SAME_GAME_GROUP : SKIP_REFUSED
}

// Add all takes the shown suggestions in order and leaves out any the server
// says cannot share a pack with one already taken (`conflicts`, read from
// either side), so one click never builds a pack the sportsbook rejects.
// `skipped` names each one left out, the suggestion it lost to, and why.
export function addAllSelection(suggestions = []) {
  const taken = []
  const skipped = []
  for (const suggestion of suggestions) {
    const id = String(suggestion.leg.outcome_id)
    const conflicts = new Set((suggestion.conflicts || []).map(String))
    const because = taken.find((kept) => (
      conflicts.has(String(kept.leg.outcome_id))
      || (kept.conflicts || []).map(String).includes(id)
    ))
    if (because) skipped.push({ suggestion, because, reason: skipReason(suggestion, because) })
    else taken.push(suggestion)
  }
  return { legs: taken.map((item) => item.leg), skipped }
}

export function visibleSuggestions(suggestions = [], expanded = false) {
  return expanded ? suggestions : suggestions.slice(0, SUGGESTION_INITIAL_VISIBLE)
}

// Reordering legs does not change what is related to them.
export function suggestionsIdentity(regulation, legs = []) {
  return JSON.stringify({
    regulation: regulation || '',
    outcomes: legs.map((leg) => String(leg.outcome_id)).sort(),
  })
}
